/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * v2 key-field strip (design-reference "Sales · Deal record": Amount, Close
 * date, Owner...): a record's few most useful values in one row under its
 * stage path. Read-only; editing stays in the record's field sections.
 * Phone, email and website values are links.
 */

import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { v2 as t } from "styles/tokens/v2";
import { linkedValue } from "./ContactValue";

export default function KeyFieldStrip({ items }) {
  return (
    <Box
      component="dl"
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
        gap: 2,
        m: 0,
        mt: 2.5,
        pt: 2.5,
        borderTop: `1px solid ${t.border}`,
        // On a record without a stage path the strip opens its card.
        "&:first-child": { mt: 0, pt: 0, borderTop: "none" },
      }}
    >
      {items.map((item) => (
        <Box key={item.label} sx={{ minWidth: 0 }}>
          <Typography component="dt" sx={{ fontSize: 13, color: t.text3, mb: 0.25 }}>
            {item.label}
          </Typography>
          <Typography
            component="dd"
            title={typeof item.value === "string" ? item.value : undefined}
            sx={{
              m: 0,
              fontSize: 15,
              fontWeight: 500,
              color: item.value ? t.text : t.text3,
              // PSA-373: wrap key-field values instead of "...".
              whiteSpace: "normal",
              wordBreak: "break-word",
              overflowWrap: "anywhere",
              lineHeight: 1.35,
            }}
          >
            {/* Phone numbers call, emails and websites open (contactLinks.js). */}
            {item.value ? (typeof item.value === "string" ? linkedValue(item.label, item.value) : item.value) : "—"}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

KeyFieldStrip.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string.isRequired, value: PropTypes.node })).isRequired,
};
